// ============================================================
// emailDelivery.js
// Why these texts matter: Gotcha #183 in CLAUDE.md.
// Turns an invitation's `lastEmail` (the latest NotificationLog row
// for that invitation, written by the ACS delivery webhook) into a
// plain-English line for the admin portal.
//
// Why this exists (2026-09-25): Resend reports success the moment
// ACS ACCEPTS the email. A refusal by the recipient's mail server
// arrives seconds later through the webhook, and nothing showed it,
// so two prospects went four sends without an invite and nobody
// could see why. The raw ACS reason is long and technical, so it is
// classified into one sentence and the raw text kept as the detail.
//
// Order matters: a sender-verify refusal ALSO says "Mailbox does not
// exist" / "mailbox unavailable" (it is OUR address it could not
// check), so it is tested before the generic mailbox case.
// ============================================================

const FAILURE_RULES = [
  // Two wordings of the same refusal: their server checked that our
  // sending domain can receive mail and it could not (20i/Stackmail
  // says "Sender verify failed"; IONOS says "invalid DNS MX or A/AAAA
  // resource record", case r1401). Fixed 2026-09-25 by giving
  // mail.a2zcarehub.co.uk an MX and a real noreply@ mailbox; if it
  // reappears, that setup has regressed.
  { test: /sender verify|invalid DNS MX or A\/AAAA/i,
    text: 'Their mail server could not verify our sending address.' },
  { test: /InfoDomainNonexistent|DNS domain .* does not exist/i,
    text: 'The email domain does not exist. Check the address.' },
  { test: /mailbox (does not exist|unavailable)|user unknown|no such user|recipient (not found|rejected)|invalid recipient|5\.1\.1|5\.1\.10/i,
    text: 'Their mail server says this mailbox does not exist. Check the address.' },
  { test: /mailbox full|quota|5\.2\.2/i,
    text: 'Their mailbox is full.' },
  { test: /spam|blocked|blacklist|blocklist|policy|5\.7\.\d/i,
    text: 'Their mail server blocked it (spam filter or policy).' },
];

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Returns null (nothing to show) or { tone: 'ok'|'pending'|'error', text, detail }.
export function describeEmailDelivery(lastEmail) {
  if (!lastEmail || !lastEmail.status) return null;
  const { status, failureReason, sentAt, deliveredAt } = lastEmail;

  if (status === 'delivered') {
    const when = formatShortDate(deliveredAt || sentAt);
    return { tone: 'ok', text: when ? `Email delivered ${when}` : 'Email delivered', detail: null };
  }
  if (status === 'sent') {
    return { tone: 'pending', text: 'Email sent, waiting for delivery confirmation', detail: null };
  }
  if (status === 'suppressed') {
    return {
      tone: 'error',
      text: 'Email not sent: this address bounced before, so sending to it is blocked.',
      detail: failureReason || null,
    };
  }
  // failed / bounced / anything unrecognised that is not a success.
  const rule = FAILURE_RULES.find((r) => failureReason && r.test.test(failureReason));
  return {
    tone: 'error',
    text: `Email not delivered: ${rule ? rule.text.charAt(0).toLowerCase() + rule.text.slice(1) : 'their mail server refused it.'}`,
    detail: failureReason || null,
  };
}
